import { AutocompleteSelect, LuneThemeNew, Text, Tile } from '@lune-fe/lune-ui-lib'

import { TransportModeListOption } from 'views/CalculateEmissions/RFQ/components/TransportModePicker'
import {
    complexAirTransportModeArray,
    complexRailTransportModeArray,
    complexRoadTransportModeArray,
    complexSeaTransportModeArray,
    simpleInlandWaterwaysTransportModeArray,
    simpleRailTransportModeArray,
    simpleSeaTransportModeArray,
    TransportMode,
    TransportModeGroup,
} from 'views/CalculateEmissions/RFQ/utils/sharedLogic'
import { TRANSPORT_MODE_LABEL } from 'views/CalculateEmissions/RFQ/utils/types'

const { palette } = LuneThemeNew

export type Props = {
    value: TransportModeListOption | undefined
    onChange: (val: TransportMode | 'Select a column' | undefined) => void
    selectedGroup: TransportModeGroup | 'Select a column' | undefined
    error?: string
    items: TransportModeListOption[]
}

const buildSeaOptions = (): TransportModeListOption[] => {
    const top: TransportModeListOption[] = complexSeaTransportModeArray.map((m: TransportMode) => ({
        value: m,
        label: TRANSPORT_MODE_LABEL[m] ?? m,
        group: '',
    }))
    const others: TransportModeListOption[] = simpleSeaTransportModeArray.map(
        (m: TransportMode) => ({
            value: m,
            label: TRANSPORT_MODE_LABEL[m] ?? m,
            group: 'Other sea shipping methods',
        }),
    )
    return [...top, ...others]
}

const buildAirOptions = (): TransportModeListOption[] =>
    [...complexAirTransportModeArray].map((m: TransportMode) => ({
        value: m,
        label: TRANSPORT_MODE_LABEL[m] ?? m,
        group: '',
    }))

const buildRoadOptions = (): TransportModeListOption[] =>
    [...complexRoadTransportModeArray].map((m: TransportMode) => ({
        value: m,
        label: TRANSPORT_MODE_LABEL[m] ?? m,
        group: '',
    }))

const buildRailOptions = (): TransportModeListOption[] => {
    const top = [...complexRailTransportModeArray].map((m: TransportMode) => ({
        value: m,
        label: TRANSPORT_MODE_LABEL[m] ?? m,
        group: '',
    }))

    const diesel = simpleRailTransportModeArray
        .filter((m) => m.startsWith('Diesel'))
        .map((m: TransportMode) => ({
            value: m,
            label: TRANSPORT_MODE_LABEL[m] ?? m,
            group: 'Other diesel methods',
        }))

    const electric = simpleRailTransportModeArray
        .filter((m) => m.startsWith('Electric'))
        .map((m: TransportMode) => ({
            value: m,
            label: TRANSPORT_MODE_LABEL[m] ?? m,
            group: 'Other electric methods',
        }))

    return [...top, ...diesel, ...electric]
}

const buildInlandWaterwaysOptions = (): TransportModeListOption[] =>
    [...simpleInlandWaterwaysTransportModeArray].map((m) => ({
        value: m as TransportMode,
        label: m,
        group: '',
    }))

const getOptionsForGroup = (
    group?: TransportModeGroup | 'Select a column',
): TransportModeListOption[] => {
    switch (group) {
        case 'Sea':
            return buildSeaOptions()
        case 'Air':
            return buildAirOptions()
        case 'Road':
            return buildRoadOptions()
        case 'Rail':
            return buildRailOptions()
        case 'Inland waterways':
            return buildInlandWaterwaysOptions()
        default:
            return []
    }
}

const ShippingMethodPicker = ({ value, error, onChange, selectedGroup }: Props) => {
    const items = getOptionsForGroup(selectedGroup)

    return (
        <AutocompleteSelect
            paperSx={{ minWidth: '280px' }}
            error={error}
            label="Shipping method"
            items={items}
            value={(items.find((i) => i.value === value?.value) ?? null) as TransportModeListOption}
            onChange={(option: TransportModeListOption | null) => {
                onChange(option?.value)
            }}
            groupBy={(option: TransportModeListOption) => option.group}
            showGroupName={true}
            customOptions={(option) => (
                <Tile sx={{ '*': { color: palette.Grey900 } }}>
                    <Text variant="body3">{option.label}</Text>
                </Tile>
            )}
        />
    )
}

export default ShippingMethodPicker
